import React, { useState } from 'react';
import { 
  KeyIcon, 
  EyeIcon, 
  EyeSlashIcon,
  CheckCircleIcon,
  ExclamationTriangleIcon,
  CogIcon,
  MoonIcon,
  SunIcon
} from '@heroicons/react/24/outline';
import { useApi } from '../../contexts/ApiContext';
import { useTheme } from '../../contexts/ThemeContext';

export const Settings: React.FC = () => {
  const { apiKey, apiBaseUrl, setApiKey, setApiBaseUrl, clearApiKey } = useApi();
  const { isDark, toggleTheme } = useTheme();
  const [tempApiKey, setTempApiKey] = useState(apiKey);
  const [tempBaseUrl, setTempBaseUrl] = useState(apiBaseUrl);
  const [showApiKey, setShowApiKey] = useState(false);
  const [isTestingConnection, setIsTestingConnection] = useState(false);
  const [connectionStatus, setConnectionStatus] = useState<'idle' | 'testing' | 'success' | 'error'>('idle');
  const [savedMessage, setSavedMessage] = useState('');

  const handleSaveApiSettings = () => {
    setApiKey(tempApiKey);
    setApiBaseUrl(tempBaseUrl);
    setSavedMessage('API settings saved successfully');
    setTimeout(() => setSavedMessage(''), 3000);
  };

  const handleTestConnection = async () => {
    if (!tempApiKey || !tempBaseUrl) {
      setConnectionStatus('error');
      return;
    }

    setIsTestingConnection(true);
    setConnectionStatus('testing');

    try {
      // Test the API connection
      const response = await fetch(`${tempBaseUrl}/models`, {
        headers: {
          'Authorization': `Bearer ${tempApiKey}`,
          'Content-Type': 'application/json'
        }
      });

      if (response.ok) {
        setConnectionStatus('success');
      } else {
        setConnectionStatus('error');
      }
    } catch (error) {
      setConnectionStatus('error');
    } finally {
      setIsTestingConnection(false);
      setTimeout(() => setConnectionStatus('idle'), 5000);
    }
  };

  const handleClearApiKey = () => {
    setTempApiKey('');
    clearApiKey();
    setSavedMessage('API key cleared');
    setTimeout(() => setSavedMessage(''), 3000);
  };

  const getConnectionStatusIcon = () => {
    switch (connectionStatus) {
      case 'testing':
        return <div className="animate-spin h-4 w-4 border-2 border-blue-500 border-t-transparent rounded-full" />;
      case 'success':
        return <CheckCircleIcon className="h-4 w-4 text-green-500" />;
      case 'error':
        return <ExclamationTriangleIcon className="h-4 w-4 text-red-500" />;
      default:
        return null;
    }
  };

  const getConnectionStatusText = () => {
    switch (connectionStatus) {
      case 'testing':
        return 'Testing connection...';
      case 'success':
        return 'Connection successful!';
      case 'error':
        return 'Connection failed. Please check your API key and URL.';
      default:
        return '';
    }
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h2 className="text-2xl font-bold text-gray-900 dark:text-gray-100">Settings</h2>
        <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">Configure your Ask Engine API settings and preferences</p>
      </div>

      {/* Success Message */}
      {savedMessage && (
        <div className="bg-green-50 dark:bg-green-900/50 border border-green-200 dark:border-green-700 rounded-md p-4">
          <div className="flex">
            <CheckCircleIcon className="h-5 w-5 text-green-400" />
            <div className="ml-3">
              <p className="text-sm font-medium text-green-800 dark:text-green-200">{savedMessage}</p>
            </div>
          </div>
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* API Configuration */}
        <div className="chart-container">
          <div className="flex items-center space-x-2 mb-4">
            <KeyIcon className="h-5 w-5 text-gray-500 dark:text-gray-400" />
            <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100">API Configuration</h3>
          </div>
          
          <div className="space-y-4">
            {/* API Base URL */}
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                API Base URL
              </label>
              <input
                type="text"
                value={tempBaseUrl}
                onChange={(e) => setTempBaseUrl(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-1 focus:ring-primary-500 focus:border-primary-500"
                placeholder="https://lllm.awareai.aware.co.th"
              />
            </div>

            {/* API Key */}
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                API Key
              </label>
              <div className="relative">
                <input
                  type={showApiKey ? 'text' : 'password'}
                  value={tempApiKey}
                  onChange={(e) => setTempApiKey(e.target.value)}
                  className="w-full px-3 py-2 pr-10 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-1 focus:ring-primary-500 focus:border-primary-500"
                  placeholder="Enter your API key..."
                />
                <button
                  type="button"
                  onClick={() => setShowApiKey(!showApiKey)}
                  className="absolute inset-y-0 right-0 pr-3 flex items-center text-gray-400 dark:text-gray-500 hover:text-gray-600 dark:hover:text-gray-300"
                >
                  {showApiKey ? (
                    <EyeSlashIcon className="h-4 w-4" />
                  ) : (
                    <EyeIcon className="h-4 w-4" />
                  )}
                </button>
              </div>
              <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                Your API key will be stored securely in your browser's local storage
              </p>
            </div>

            {/* Connection Status */}
            {connectionStatus !== 'idle' && (
              <div className="flex items-center space-x-2 p-3 rounded-md bg-gray-50 dark:bg-gray-700">
                {getConnectionStatusIcon()}
                <span className={`text-sm ${
                  connectionStatus === 'success' ? 'text-green-600 dark:text-green-400' :
                  connectionStatus === 'error' ? 'text-red-600 dark:text-red-400' :
                  'text-blue-600 dark:text-blue-400'
                }`}>
                  {getConnectionStatusText()}
                </span>
              </div>
            )}

            {/* Action Buttons */}
            <div className="flex space-x-3 pt-4">
              <button
                onClick={handleTestConnection}
                disabled={isTestingConnection || !tempApiKey || !tempBaseUrl}
                className="btn-secondary disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isTestingConnection ? 'Testing...' : 'Test Connection'}
              </button>
              <button
                onClick={handleSaveApiSettings}
                className="btn-primary"
              >
                Save Settings
              </button>
              <button
                onClick={handleClearApiKey}
                className="text-red-600 dark:text-red-400 hover:text-red-700 dark:hover:text-red-300 px-3 py-2 text-sm font-medium"
              >
                Clear API Key
              </button>
            </div>
          </div>
        </div>

        {/* Appearance Settings */}
        <div className="chart-container">
          <div className="flex items-center space-x-2 mb-4">
            <CogIcon className="h-5 w-5 text-gray-500 dark:text-gray-400" />
            <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100">Appearance</h3>
          </div>
          
          <div className="space-y-4">
            {/* Theme Toggle */}
            <div className="flex items-center justify-between p-4 border border-gray-200 dark:border-gray-600 rounded-lg">
              <div className="flex items-center space-x-3">
                {isDark ? (
                  <MoonIcon className="h-5 w-5 text-gray-600 dark:text-gray-400" />
                ) : (
                  <SunIcon className="h-5 w-5 text-gray-600 dark:text-gray-400" />
                )}
                <div>
                  <p className="text-sm font-medium text-gray-900 dark:text-gray-100">
                    {isDark ? 'Dark Mode' : 'Light Mode'}
                  </p>
                  <p className="text-xs text-gray-500 dark:text-gray-400">
                    {isDark ? 'Currently using dark theme' : 'Currently using light theme'}
                  </p>
                </div>
              </div>
              <button
                onClick={toggleTheme}
                className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2 dark:focus:ring-offset-gray-800 ${
                  isDark ? 'bg-primary-600' : 'bg-gray-200'
                }`}
              >
                <span
                  className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                    isDark ? 'translate-x-6' : 'translate-x-1'
                  }`}
                />
              </button>
            </div>

            {/* Data Refresh Settings */}
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Data Refresh Interval
              </label>
              <select className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-1 focus:ring-primary-500 focus:border-primary-500">
                <option value="30">30 seconds</option>
                <option value="60" selected>1 minute</option>
                <option value="300">5 minutes</option>
                <option value="600">10 minutes</option>
                <option value="0">Manual only</option>
              </select>
            </div>

            {/* Notifications */}
            <div className="space-y-3">
              <p className="text-sm font-medium text-gray-700 dark:text-gray-300">Notifications</p>
              <div className="space-y-2">
                <label className="flex items-center">
                  <input 
                    type="checkbox" 
                    className="rounded border-gray-300 dark:border-gray-600 text-primary-600 shadow-sm focus:border-primary-300 focus:ring focus:ring-primary-200 focus:ring-opacity-50" 
                    defaultChecked 
                  />
                  <span className="ml-2 text-sm text-gray-600 dark:text-gray-400">Usage alerts</span>
                </label>
                <label className="flex items-center">
                  <input 
                    type="checkbox" 
                    className="rounded border-gray-300 dark:border-gray-600 text-primary-600 shadow-sm focus:border-primary-300 focus:ring focus:ring-primary-200 focus:ring-opacity-50" 
                    defaultChecked 
                  />
                  <span className="ml-2 text-sm text-gray-600 dark:text-gray-400">Cost warnings</span>
                </label>
                <label className="flex items-center">
                  <input 
                    type="checkbox" 
                    className="rounded border-gray-300 dark:border-gray-600 text-primary-600 shadow-sm focus:border-primary-300 focus:ring focus:ring-primary-200 focus:ring-opacity-50" 
                  />
                  <span className="ml-2 text-sm text-gray-600 dark:text-gray-400">Weekly reports</span>
                </label>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* API Status */}
      <div className="chart-container">
        <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-4">Current API Status</h3>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div className="text-center p-4 border border-gray-200 dark:border-gray-600 rounded-lg">
            <p className="text-sm text-gray-600 dark:text-gray-400">Connection Status</p>
            <p className="text-lg font-semibold text-gray-900 dark:text-gray-100 mt-1">
              {apiKey ? 'Configured' : 'Not Configured'}
            </p>
          </div>
          <div className="text-center p-4 border border-gray-200 dark:border-gray-600 rounded-lg">
            <p className="text-sm text-gray-600 dark:text-gray-400">Data Source</p>
            <p className="text-lg font-semibold text-gray-900 dark:text-gray-100 mt-1">
              {apiKey ? 'Live API' : 'Mock Data'}
            </p>
          </div>
          <div className="text-center p-4 border border-gray-200 dark:border-gray-600 rounded-lg">
            <p className="text-sm text-gray-600 dark:text-gray-400">Last Updated</p>
            <p className="text-lg font-semibold text-gray-900 dark:text-gray-100 mt-1">
              {apiKey ? 'Real-time' : 'Static'}
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};